import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CardMedia from "@mui/material/CardMedia";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Header from "../core/Header";
import Footer from "../core/Footer";
import { autecProducts } from "../data/autecData";
import { useTranslation } from "../utils/i18n";

export default function AutecProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, resolve } = useTranslation();

  const product = autecProducts.find((p) => p.slug === slug);
  const mainImage = product?.image || "/images/products/AUTEC.jpg";
  const [activeImage, setActiveImage] = useState(mainImage);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    setActiveImage(mainImage);
  }, [mainImage]);

  const galleryItems = useMemo(() => {
    if (Array.isArray(product?.gallery) && product.gallery.length > 0) {
      return product.gallery.map((image) => ({ image }));
    }
    return [{ image: mainImage }, { image: mainImage }, { image: mainImage }];
  }, [mainImage, product?.gallery]);

  const relatedProducts = useMemo(() => {
    if (!product) return [];
    const oppositeCategory =
      product.category === "Mottagarenheter"
        ? "Sändarenheter"
        : "Mottagarenheter";
    return autecProducts.filter(
      (p) => p.family === product.family && p.category === oppositeCategory,
    );
  }, [product]);

  const relatedHeading =
    product?.category === "Mottagarenheter"
      ? t("autecDetail.relatedTransmitters")
      : t("autecDetail.relatedReceivers");

  // TODO: mock generated paragraphs — replace with real product descriptions in data/autecData.js
  const descriptionParagraphs = useMemo(() => {
    if (!product) return [];
    return [resolve(product.shortDescription)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, resolve]);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        bgcolor: "#efefef",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Header />

      <Box
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: 1440,
          mx: "auto",
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 10.5, md: 13 },
          pb: { xs: 5, md: 8 },
        }}
      >
        {!product ? (
          <Box
            sx={{
              p: { xs: 3, md: 4 },
              bgcolor: "transparent",
              borderRadius: 2,
            }}
          >
            <Typography variant="h6">{t("autecDetail.notFound")}</Typography>
          </Box>
        ) : (
          <>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1.75 }}>
              <Link
                component={RouterLink}
                to="/"
                underline="none"
                sx={{ color: "#4b4b4b" }}
              >
                {t("autecDetail.breadcrumbHome")}
              </Link>
              <Link
                component={RouterLink}
                to="/autecproducts"
                state={{ restoreScroll: true }}
                underline="none"
                sx={{ color: "#4b4b4b" }}
              >
                {t("autecDetail.breadcrumbProducts")}
              </Link>
              <Typography aria-current="page" sx={{ color: "#FF6A00" }}>
                {resolve(product.name)}
              </Typography>
            </Breadcrumbs>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(0, 1.05fr) minmax(360px, 0.95fr)",
                },
                gap: { xs: 3, md: 4 },
                alignItems: "start",
                mt: 7,
              }}
            >
              <Box
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 1,
                  pb: { xs: 2, md: 2.5 },
                }}
              >
                <Box
                  onClick={() => {
                    const idx = galleryItems.findIndex(
                      (item) => item.image === activeImage,
                    );
                    setLightboxIndex(idx >= 0 ? idx : 0);
                    setLightboxOpen(true);
                  }}
                  sx={{
                    borderRadius: 2,
                    minHeight: { xs: 320, md: 520 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    bgcolor: "transparent",
                    cursor: "zoom-in",
                  }}
                >
                  <CardMedia
                    component="img"
                    image={activeImage}
                    alt={resolve(product.name)}
                    sx={{
                      width: "100%",
                      maxWidth: { xs: 280, md: 420 },
                      maxHeight: { xs: 280, md: 420 },
                      objectFit: "contain",
                      px: 2,
                      py: 3,
                    }}
                  />
                </Box>

                <Dialog
                  open={lightboxOpen}
                  onClose={() => setLightboxOpen(false)}
                  fullScreen
                  PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.65)" } }}
                >
                  <IconButton
                    onClick={() => setLightboxOpen(false)}
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      color: "#fff",
                      zIndex: 2,
                      bgcolor: "rgba(255,255,255,0.1)",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    <CloseIcon />
                  </IconButton>

                  <DialogContent
                    onClick={() => setLightboxOpen(false)} // clicking anywhere outside the image closes it
                    sx={{
                      p: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      onClick={(e) => e.stopPropagation()} // clicking the image (or arrows) shouldn't close it
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        maxWidth: "100%",
                        maxHeight: "100vh",
                      }}
                    >
                      <Box
                        component="img"
                        src={galleryItems[lightboxIndex]?.image || activeImage}
                        alt={resolve(product.name)}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100vh",
                          objectFit: "contain",
                          display: "block",
                        }}
                      />

                      {galleryItems.length > 1 && (
                        <>
                          <IconButton
                            onClick={() => setLightboxIndex((i) => i - 1)}
                            disabled={lightboxIndex === 0}
                            sx={{
                              position: "absolute",
                              left: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#000",
                              zIndex: 2,
                              bgcolor: "rgba(255,255,255,0.7)",
                              "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                              "&.Mui-disabled": {
                                opacity: 0.25,
                                color: "#000",
                              },
                            }}
                          >
                            <ArrowBackIosNewIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => setLightboxIndex((i) => i + 1)}
                            disabled={lightboxIndex === galleryItems.length - 1}
                            sx={{
                              position: "absolute",
                              right: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#000",
                              zIndex: 2,
                              bgcolor: "rgba(255,255,255,0.7)",
                              "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                              "&.Mui-disabled": {
                                opacity: 0.25,
                                color: "#000",
                              },
                            }}
                          >
                            <ArrowForwardIosIcon />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </DialogContent>
                </Dialog>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  {galleryItems.map((item, index) => {
                    const isActive = activeImage === item.image;
                    return (
                      <Box
                        key={`${item.image}-${index}`}
                        onClick={() => setActiveImage(item.image)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === "Enter" && setActiveImage(item.image)
                        }
                        sx={{
                          cursor: "pointer",
                          borderRadius: 1.5,
                          bgcolor: "#fff",
                          minHeight: 70,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 0.75,
                          border: isActive
                            ? "2px solid #FF6A00"
                            : "2px solid transparent",
                          transition: "border-color 150ms ease",
                          "&:hover": {
                            border: "2px solid rgba(244,127,63,0.5)",
                          },
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={item.image}
                          alt={`${resolve(product.name)} ${index + 1}`}
                          sx={{ objectFit: "contain", maxHeight: 56 }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              <Box
                sx={{
                  borderRadius: 2,
                  p: { xs: 2.5, md: 4 },
                }}
              >
                <Typography
                  sx={{
                    color: "#FF6A00",
                    fontSize: "0.82rem",
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    mb: 1,
                  }}
                >
                  {t("autecDetail.label")}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.04em",
                    lineHeight: 1.05,
                    color: "#1e1a18",
                    fontSize: { xs: "2.1rem", md: "3rem" },
                    mb: 2,
                  }}
                >
                  {resolve(product.name)}
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(30, 26, 24, 0.75)",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.98rem", md: "1.03rem" },
                    mb: 2.5,
                  }}
                >
                  {resolve(product.shortDescription)}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                    mb: 3,
                  }}
                >
                  {product.features.map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        py: 3,
                        borderBottom:
                          index < product.features.length - 1
                            ? "1px solid rgba(30, 26, 24, 0.12)"
                            : "none",
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: "#FF6A00", mt: 0.2, fontSize: 25 }}
                      />
                      <Typography
                        sx={{
                          color: "#2b241f",
                          lineHeight: 1.6,
                          fontSize: { xs: "1.02rem", md: "1.06rem" },
                        }}
                      >
                        {resolve(feature)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                mt: { xs: 2.5, md: 3 },
                borderTop: "1px solid rgba(30, 26, 24, 0.18)",
              }}
            />
            <Box
              sx={{
                mt: { xs: 3, md: 4 },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
                gap: { xs: 3, md: 4 },
              }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  p: { xs: 2.25, md: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.02em",
                    mb: 1.25,
                    fontSize: "1.1rem",
                  }}
                >
                  {t("autecDetail.descriptionTitle")}
                </Typography>
                {/* TODO: mock generated paragraphs — replace with real content in data/autecData.js */}
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.4 }}
                >
                  {descriptionParagraphs.map((paragraph, index) => (
                    <Typography
                      key={`${product.slug}-description-${index}`}
                      sx={{ color: "rgba(30, 26, 24, 0.8)", lineHeight: 1.7 }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  borderRadius: 2,
                  p: { xs: 2.25, md: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "0.02em",
                    mb: 1.25,
                    fontSize: "1.1rem",
                  }}
                >
                  {t("autecDetail.technicalTitle")}
                </Typography>

                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    bgcolor: "transparent",
                    boxShadow: "none",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    size="small"
                    sx={{ border: "1px solid rgba(30, 26, 24, 0.16)" }}
                  >
                    <TableBody>
                      {product.technicalData.map((row, i) => (
                        <TableRow key={i}>
                          <TableCell
                            sx={{
                              width: { md: 240 },
                              fontWeight: 700,
                              bgcolor: "rgba(30, 26, 24, 0.03)",
                              borderRight: "1px solid rgba(30, 26, 24, 0.16)",
                              borderBottom: "1px solid rgba(30, 26, 24, 0.16)",
                            }}
                          >
                            {resolve(row.label)}
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: "1px solid rgba(30, 26, 24, 0.16)",
                            }}
                          >
                            {resolve(row.value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>

            {relatedProducts.length > 0 && (
              <Box sx={{ mt: { xs: 3, md: 4 } }}>
                <Box
                  sx={{
                    borderTop: "1px solid rgba(30, 26, 24, 0.18)",
                    mb: { xs: 3, md: 4 },
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "1.05rem",
                    mb: 2.5,
                    color: "#0B0F13",
                  }}
                >
                  {relatedHeading}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(3, 1fr)",
                      md: "repeat(4, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  {relatedProducts.map((rp) => (
                    <Box
                      key={rp.slug}
                      onClick={() => navigate(`/autecproducts/${rp.slug}`)}
                      sx={{
                        bgcolor: "#fff",
                        border: "1px solid #E3E6EB",
                        borderRadius: 2,
                        p: 2,
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        transition:
                          "border-color 150ms ease, box-shadow 150ms ease",
                        "&:hover": {
                          borderColor: "#FF6A00",
                          boxShadow: "0 4px 12px rgba(244,127,63,0.15)",
                        },
                      }}
                    >
                      <Box
                        component="img"
                        src={rp.image}
                        alt={resolve(rp.name)}
                        sx={{
                          width: "100%",
                          maxHeight: 100,
                          objectFit: "contain",
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: "#0B0F13",
                            lineHeight: 1.3,
                          }}
                        >
                          {resolve(rp.name)}
                        </Typography>
                        <ArrowForwardIcon
                          sx={{ fontSize: 16, color: "#FF6A00", flexShrink: 0 }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box
              sx={{
                mt: { xs: 3, md: 4 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.25,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/contact")}
                startIcon={<MailOutlineIcon />}
                sx={{
                  borderColor: "rgba(30, 26, 24, 0.22)",
                  color: "#1e1a18",
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.1,
                  px: 2.75,
                  bgcolor: "transparent",
                }}
              >
                {t("autecDetail.contact")}
              </Button>

              <Button
                variant="outlined"
                onClick={() =>
                  navigate("/contact", {
                    state: {
                      subject: product?.name?.sv ?? resolve(product.name),
                    },
                  })
                }
                startIcon={<RequestQuoteOutlinedIcon />}
                sx={{
                  borderColor: "rgba(30, 26, 24, 0.22)",
                  color: "#1e1a18",
                  borderRadius: 1.5,
                  textTransform: "none",
                  fontWeight: 700,
                  py: 1.1,
                  px: 2.75,
                  bgcolor: "transparent",
                }}
              >
                {t("autecDetail.quote")}
              </Button>
            </Box>
          </>
        )}
      </Box>
      <Footer />
    </Box>
  );
}
